/**
 * Service orders — business data, read through the BFF (never PostgREST; see
 * `api/notifications.ts`'s module doc for that split, ADR-0004/ADR-0005).
 *
 * Every function here is pure with respect to React: it takes `fetch` and the
 * BFF base URL as explicit parameters and returns a closed `{ data, error }`
 * result, following `api/notifications.ts`'s house shape — never throws, so a
 * caller decides how to degrade (`auth/loginFlow.ts` and `bffAuth.ts` use the
 * same injection pattern for the same reason: testable without a network).
 *
 * The BFF forwards the platform envelope unchanged (`{ success, dados, error
 * }`, ADR-0014 §3 as implemented in `apps/bff/src/envelope.ts`) — every call
 * here unwraps it and never leaks `error.detail` (log-only, per
 * `docs/contracts/conventions.md`) to the UI. The BFF supplies the customer's
 * `documento` from the verified session; it is never sent from here.
 *
 * ---------------------------------------------------------------------------
 * ⛔ THE ITEM-STATE RULE — read before touching anything below that renders
 * an item.
 *
 * The API can (correctly) return an order at `in_production` whose item is
 * still `received`, because item `status` reflects an internal workflow gate
 * (has the entry report been confirmed?) that lags the production `steps`.
 * That is not a bug on either side — but showing a customer three signals
 * that disagree (order status, item status, step timeline) on one screen
 * reads as broken.
 *
 * The settled decision (from `docs/handoff/2026-07-30-read-endpoints-shipped.md`):
 * customer-facing item PROGRESS is derived from `steps` (`deriveItemProgress`
 * below); `status` is never used to describe an item's progress — the wire
 * `status` field only powers the order-level chip (`StatusChip`, sourced from
 * `order.status`). Do not "fix" this by wiring `item.status` into a chip; that
 * reintroduces the exact disagreement this module exists to avoid.
 */

// ---------------------------------------------------------------------------
// Wire types — English field names, matching the BFF's fixed response shape.
// ---------------------------------------------------------------------------

export interface Franchise {
  id: string;
  name: string;
  /** Always null on the list; unconfirmed on detail. Never build on either. */
  city: string | null;
  state: string | null;
}

/**
 * The eight internal states collapse server-side to five customer-facing
 * ones plus a neutral `in_progress` fallback for anything unrecognised — see
 * `KNOWN_STATUSES` / `statusKey` below. Kept as `string` on the wire type
 * because the vocabulary is explicitly open-ended.
 */
export type ServiceOrderStatus = string;

export interface ServiceOrderListItem {
  order_id: number;
  franchise: Franchise;
  status: ServiceOrderStatus;
  created_at: string;
  total_cents: number;
  currency: string;
  items_count: number;
  delivered_items_count: number;
  expected_delivery_at: string | null;
  first_item_description: string;
  /** Null on the list; populated on `get`. */
  subtotal_cents: number | null;
}

/** Same row shape as the list, with `subtotal_cents` populated. */
export type ServiceOrderDetailRow = ServiceOrderListItem;

export type StepStatus = 'pending' | 'in_progress' | 'completed' | 'skipped';

export interface ServiceOrderStep {
  name: string;
  position: number;
  status: StepStatus;
  started_at: string | null;
  completed_at: string | null;
}

export interface ReportSummary {
  report_id: number;
  confirmed: boolean;
  confirmed_at: string | null;
  /** Exit-only; null on an entry report summary. */
  final_status: string | null;
}

export type ServiceOrderItemType = 'service' | 'additional_service' | 'product';

export interface ServiceOrderItem {
  item_id: number;
  /** Set only on `additional_service` rows — the nesting key. */
  parent_item_id: number | null;
  type: ServiceOrderItemType;
  description: string;
  quantity: number;
  unit_price_cents: number;
  total_cents: number;
  status: ServiceOrderStatus;
  expected_delivery_at: string | null;
  delivered_at: string | null;
  qrcode: string | null;
  /** Empty for `product` rows, which carry no production timeline. */
  steps: ServiceOrderStep[];
  /** Absent or null-filled for `product` rows, which carry no laudos. */
  reports?: { entry: ReportSummary | null; exit: ReportSummary | null };
}

export interface ServiceOrderDetail {
  order: ServiceOrderDetailRow;
  items: ServiceOrderItem[];
}

export type ReportAttributeType = 'photo' | 'text' | 'textarea' | 'select' | 'checkbox';

export interface ReportAttribute {
  attribute_id: number;
  name: string;
  type: ReportAttributeType;
  /**
   * For `photo`, the image URL (signed or platform-hosted). For `checkbox`,
   * the normalized wire value `yes` | `no` — checkboxes are ALWAYS present,
   * even untouched ones (absence means "no", and the customer must see that).
   */
  value: string;
}

export interface ReportItemAttribute {
  attribute_id: number;
  name: string;
  /** so_item_attribute_type — 'boolean' carries the normalized `yes` | `no`. */
  type: 'text' | 'textarea' | 'select' | 'boolean' | string;
  value: string;
}

export interface ReportDetail {
  order_item_id: number;
  order_id: number;
  type: 'entry' | 'exit';
  franchise: Franchise;
  item_description: string;
  item_qrcode: string | null;
  status: 'confirmed' | 'pending';
  confirmed_at: string | null;
  final_status: string | null;
  observations: string | null;
  /** Attributes with no value are omitted entirely by the API — never render a placeholder for a missing one. */
  attributes: ReportAttribute[];
  /** Item attributes (marca, modelo, ...) — absent on older API builds; treat as empty. */
  item_attributes?: ReportItemAttribute[];
}

// ---------------------------------------------------------------------------
// Status vocabulary
// ---------------------------------------------------------------------------

const KNOWN_STATUSES = [
  'received',
  'in_production',
  'ready_for_pickup',
  'delivered',
  'cancelled',
] as const;

export type KnownStatus = (typeof KNOWN_STATUSES)[number];
export type StatusKey = KnownStatus | 'in_progress';

/**
 * Maps a wire status to one of the five customer-facing statuses, or the
 * neutral `in_progress` fallback for anything this catalogue does not (yet)
 * recognise. `in_progress` is a REAL value the API sends deliberately for any
 * unmapped internal state — never render an empty chip for it.
 */
export function statusKey(status: ServiceOrderStatus): StatusKey {
  return (KNOWN_STATUSES as readonly string[]).includes(status) ? (status as KnownStatus) : 'in_progress';
}

// ---------------------------------------------------------------------------
// Item progress — derived from `steps`, never from `status`. See the module
// doc's ⛔ rule above.
// ---------------------------------------------------------------------------

export type ItemProgress = 'none' | 'not_started' | 'in_progress' | 'completed';

/**
 * `none` — a `product` item, which carries no steps at all; nothing to show.
 * `not_started` — every step is still `pending`.
 * `completed` — every step is `completed` or `skipped`.
 * `in_progress` — anything in between, including the documented case where
 *   the internal `reprocess_pending` (rework) state is reported as a step's
 *   `in_progress` deliberately (customers are never shown "we redid this").
 */
export function deriveItemProgress(steps: ServiceOrderStep[]): ItemProgress {
  if (steps.length === 0) return 'none';
  if (steps.every((s) => s.status === 'completed' || s.status === 'skipped')) return 'completed';
  if (steps.some((s) => s.status === 'in_progress' || s.status === 'completed')) return 'in_progress';
  return 'not_started';
}

// ---------------------------------------------------------------------------
// Item tree — additional_service rows nest under their parent, never as
// siblings (CLAUDE.md / the design brief, both explicit about this).
// ---------------------------------------------------------------------------

export interface ItemNode {
  item: ServiceOrderItem;
  children: ServiceOrderItem[];
}

/**
 * Groups `additional_service` rows under their `parent_item_id`. An
 * `additional_service` whose declared parent is missing from the page (the
 * "unreachable NULL-parent edge" the handoff flags as an open ticket on the
 * API side) is rendered as a root rather than dropped or thrown on — a
 * customer should still see every item they paid for.
 */
export function buildItemTree(items: ServiceOrderItem[]): ItemNode[] {
  const childrenByParent = new Map<number, ServiceOrderItem[]>();
  for (const item of items) {
    if (item.parent_item_id !== null) {
      const siblings = childrenByParent.get(item.parent_item_id) ?? [];
      siblings.push(item);
      childrenByParent.set(item.parent_item_id, siblings);
    }
  }

  const knownIds = new Set(items.map((i) => i.item_id));

  return items
    .filter((item) => item.parent_item_id === null || !knownIds.has(item.parent_item_id))
    .map((item) => ({ item, children: childrenByParent.get(item.item_id) ?? [] }));
}

// ---------------------------------------------------------------------------
// HTTP
// ---------------------------------------------------------------------------

export type FetchLike = typeof fetch;

/** Small, closed error set — never the raw upstream message (may carry [PD]-adjacent detail meant for logs only). */
export type ServiceOrdersError = 'network' | 'not_found' | 'server';

interface Envelope<T> {
  success: boolean;
  dados: T | null;
  error: { code?: string } | null;
}

interface CallResult<T> {
  data: T | null;
  error: ServiceOrdersError | null;
}

async function callBff<T>(fetchImpl: FetchLike, url: string, accessToken: string): Promise<CallResult<T>> {
  let res: Response;
  try {
    res = await fetchImpl(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  } catch {
    return { data: null, error: 'network' };
  }

  if (res.status === 404) {
    // The two 404 reasons ("belongs to someone else" / "does not exist") are
    // deliberately indistinguishable — never try to infer which one happened.
    return { data: null, error: 'not_found' };
  }

  let body: Envelope<T> | null = null;
  try {
    body = (await res.json()) as Envelope<T>;
  } catch {
    body = null;
  }

  if (!res.ok || !body || body.success !== true || body.dados === null || body.dados === undefined) {
    return { data: null, error: 'server' };
  }

  return { data: body.dados, error: null };
}

export interface ListServiceOrdersResult {
  data: ServiceOrderListItem[];
  nextCursor: string | null;
  error: ServiceOrdersError | null;
}

/**
 * Lists the signed-in customer's service orders, newest first
 * (`created_at DESC, order_id DESC`, the API's own ordering — this function
 * does not re-sort). Zero orders is `{ data: [], nextCursor: null, error:
 * null }`, never an error (CLAUDE.md §2: zero matches is normal).
 */
export async function listServiceOrders(
  fetchImpl: FetchLike,
  bffBase: string,
  accessToken: string,
  cursor: string | null = null,
  limit = 20,
): Promise<ListServiceOrdersResult> {
  const qs = new URLSearchParams();
  if (cursor) qs.set('cursor', cursor);
  qs.set('limit', String(limit));

  const { data, error } = await callBff<{ data: ServiceOrderListItem[]; next_cursor: string | null }>(
    fetchImpl,
    `${bffBase}/service-orders?${qs.toString()}`,
    accessToken,
  );

  if (error || !data) {
    return { data: [], nextCursor: null, error: error ?? 'server' };
  }
  return { data: data.data, nextCursor: data.next_cursor, error: null };
}

export interface GetServiceOrderResult {
  data: ServiceOrderDetail | null;
  error: ServiceOrdersError | null;
}

/** Fetches one order and its items, scoped to the signed-in customer. */
export async function getServiceOrder(
  fetchImpl: FetchLike,
  bffBase: string,
  accessToken: string,
  orderId: string,
): Promise<GetServiceOrderResult> {
  const { data, error } = await callBff<ServiceOrderDetail>(
    fetchImpl,
    `${bffBase}/service-orders/${encodeURIComponent(orderId)}`,
    accessToken,
  );
  return { data: error ? null : data, error };
}

export interface GetReportResult {
  data: ReportDetail | null;
  error: ServiceOrdersError | null;
}

/**
 * Fetches one laudo (entry or exit) for one item. Not yet wired into any page
 * — the full-screen report viewer is `docs/handoff/service-orders-design-
 * brief.md`'s screen 4, which has no route in this milestone — but the data
 * layer is built against the fixed API shape now so that screen is a UI-only
 * follow-up.
 */
export async function getReport(
  fetchImpl: FetchLike,
  bffBase: string,
  accessToken: string,
  orderItemId: string,
  type: 'entry' | 'exit',
): Promise<GetReportResult> {
  const { data, error } = await callBff<ReportDetail>(
    fetchImpl,
    `${bffBase}/reports/${encodeURIComponent(orderItemId)}?type=${type}`,
    accessToken,
  );
  return { data: error ? null : data, error };
}
